import { Address, Alternative, CountryCode } from '@guiker/base-entity'
import { capitalize } from '@guiker/lodash'

import { extractStreetName, uniqueStrings } from './utils'

type ComponentName = {
  text: string
  languageCode: string
}

type AddressComponent = {
  componentName: ComponentName
  componentType: string
  confirmationLevel: string
}

type Geocode = {
  location: {
    latitude: number
    longitude: number
  }
  placeId: string
}

type AddressValidationResponse = {
  responseId: string
  result: {
    verdict: {
      addressComplete: boolean
      hasInferredComponents?: boolean
      hasUnconfirmedComponents: boolean
    }
    address: {
      formattedAddress: string
      postalAddress: {
        regionCode: string //CLDR region code ie. 'CA', 'US'
        languageCode: string // en
        postalCode: string
        locality: string // city
        addressLines: string[]
        administrativeArea?: string //state, province, ie. switzerland is blank
      }
      addressComponents: AddressComponent[]
    }
    geocode: Geocode
    metadata: {
      business: boolean
      residential: boolean
    }
    uspsData?: {
      // only for US addresses
      standardizedAddress: {
        firstAddressLine: string
        firm: string
        secondAddressLine: string
        urbanization: string
        cityStateZipAddressLine: string
        city: string
        state: string
        zipCode: string
        zipCodeExtension: string
      }
    }
  }
}

type PostalAddress = {
  revision?: number
  regionCode: string //CLDR region code ie. 'CA', 'US'
  languageCode?: string
  postalCode?: string
  sortingCode?: string
  administrativeArea?: string
  locality?: string // city
  sublocality?: string
  addressLines: string[]
  recipients?: string[]
  organization?: string
}

export const findAddressComponent = (res: AddressValidationResponse['result'], fieldName: String): string => {
  return res.address.addressComponents.find((comp) => comp.componentType === fieldName)?.componentName?.text
}

const findAlternatives = ({
  address,
  res,
}: {
  address: Omit<Address, 'city'>
  res: AddressValidationResponse['result']
}): Alternative => {
  const postalAddress = res.address.postalAddress
  const uspsAddress = res.uspsData

  return {
    zipcode: uniqueStrings([
      !!uspsAddress ? postalAddress.postalCode.split('-')[0] : undefined,
      uspsAddress?.standardizedAddress?.zipCode,
      findAddressComponent(res, 'postal_code'),
      address.postalCode,
    ]),
    city: uniqueStrings([
      uspsAddress ? capitalize(uspsAddress?.standardizedAddress?.city) : undefined,
      findAddressComponent(res, 'locality'),
      ...(address.cityName ? [address.cityName] : []),
    ]),
    street: uniqueStrings([
      extractStreetName(postalAddress.addressLines[0], address.streetNumber),
      findAddressComponent(res, 'route'),
      address.street,
    ]),
  }
}

export const transformAddressValidationResponseToBaseAddress = ({
  res,
}: {
  res: AddressValidationResponse['result']
}): {
  address: Omit<Address, 'city'>
  alternatives: Alternative
  geocode: Geocode
} => {
  const postalAddress = res.address.postalAddress
  const streetNumber = findAddressComponent(res, 'street_number')
  const address = {
    cityName: postalAddress.locality,
    country: postalAddress.regionCode as CountryCode,
    state: postalAddress.administrativeArea,
    street: extractStreetName(postalAddress.addressLines[0], streetNumber),
    streetNumber,
    postalCode: findAddressComponent(res, 'postal_code'),
  } as Omit<Address, 'city'>

  const alternatives = findAlternatives({ address, res })

  const geocode = {
    location: {
      latitude: res.geocode.location.latitude,
      longitude: res.geocode.location.longitude,
    },
    placeId: res.geocode.placeId,
  }

  return {
    address,
    alternatives,
    geocode,
  }
}

export { PostalAddress, AddressValidationResponse, AddressComponent, Geocode }
